import '@app/App.css';
import WorkspaceHeader from '@app/header/AppHeader';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@comps/ui/resizable';
import { isCompozzError } from '@dto/CompozzError';
import { Dashboard } from '@dto/Dashboard';
import { checkedSelection } from '@dto/Users';
import { ObjectInfos, ObjectResponse } from '@dto/objects';
import { UserPreferences } from '@dto/preferences';
import { View } from '@dto/views';
import { Workspace } from '@dto/workspaces';
import { APP_SIDEBAR_COLUMN_SIZE, BASE_URL, SELECTED_WORKSPACE_ID } from '@utils/constants';
import { getSelectedDashboardKey, getSelectedObjectKey, getSelectedViewKey, updateStorageSelection } from '@utils/localStorage';
import { fetchWorkspacePreferences } from '@utils/preferencesApi';
import { TW_App, TW_AppMain } from '@utils/twClasses';
import { get } from '@utils/utils';
import { enUS, fr } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImperativePanelHandle } from 'react-resizable-panels';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppStore, useModelStore } from 'src/store';
import LoadingWorkspaceSection from './LoadingWorkspaceSection';
import Dashboards from './dashboards/Dashboards';
import DriveWorkspaceLayout from './drive/DriveWorkspaceLayout';
import Members from './members/Members';
import Roles from './members/Roles';
import ObjectModelView from './objects/ObjectModelView';
import AppSidebar from './sidebar/AppSidebar';

function WorkspaceSection() {

  const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const selectedDashboard = useModelStore((state) => state.selectedDashboard);
  const setWorkspaces = useModelStore((state) => state.updateWorkspaces);
  const setObjectInfos = useModelStore((state) => state.updateObjectInfos);
  const setSelectedWorkspace = useModelStore((state) => state.selectWorkspace);
  const updateLocales = useAppStore(state => state.updateLocales);
  const fetchUsers = useAppStore(state => state.fetchUsers);
  const [loading, setLoading] = useState(true);
  const [sideBarOpen, setSideBarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { wsKey } = useParams();
  const [sidebarColumnSize, setSidebarColumnSize] = useState(10);
  const leftPanelRef = useRef<ImperativePanelHandle>(null);
  const updateUserRole = useModelStore(state => state.updateUserRole);
  const fetchUserRole = useModelStore(state => state.fetchUserRole);
  const fetchDashboards = useModelStore(state => state.fetchDashboards);
  const setWorkspacePreferences = useModelStore(state => state.setWorkspacePreferences);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [maxSidebarSize, setMaxSidebarSize] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);


  const handleResize = () => {
    const newWidth = window.innerWidth;
    setIsMobile(newWidth < 640);
    defineSizebarMaxSize(newWidth);
  };

  const defineSizebarMaxSize = (width: number) => {
    if (width < 640) {
      setMaxSidebarSize(0);
    } else if (width >= 640 && width < 720) {
      setMaxSidebarSize(3);
    } else if (width >= 720 && width < 900) {
      setMaxSidebarSize(2);
    } else if (width >= 900 && width < 1400) {
      setMaxSidebarSize(1.5);
    } else {
      setMaxSidebarSize(1);
    }
  }

  useEffect(() => {
    updateLocales(new Map([['fr-FR', fr], ['en-EN', enUS]]));
    fetchUsers();
    fetchWorkspaces();
    defineSizebarMaxSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  useEffect(() => {

    fetchUserRole(selectedWorkspace?._key);

    fetchDashboards(selectedWorkspace?._key, t);

    fetchObjects().then(() => {
      setLoading(false);
    });

    fetchPreferences();

    const sidebarColumnSizeStr = localStorage.getItem(APP_SIDEBAR_COLUMN_SIZE + selectedWorkspace?._key);
    if (sidebarColumnSizeStr) {
      setSidebarColumnSize(Number(sidebarColumnSizeStr));
    }
  }, [selectedWorkspace]);


  useEffect(() => {
    if (sideBarOpen) {
      const sidebarColumnSizeStr = localStorage.getItem(APP_SIDEBAR_COLUMN_SIZE + selectedWorkspace?._key);
      if (sidebarColumnSizeStr) {
        setSidebarColumnSize(Number(sidebarColumnSizeStr));
        setTimeout(() => {
          leftPanelRef.current?.resize(sidebarColumnSize);
        }, 0);
      }
    }
  }, [sideBarOpen])

  useEffect(() => {
    setTimeout(() => {
      leftPanelRef.current?.resize(sidebarColumnSize);
    }, 0);
  }, [sidebarColumnSize]);

  useEffect(() => {
    if (selectedWorkspace) {
      if (selectedDashboard && !location.pathname.includes(`/workspaces/${selectedWorkspace._key}/dashboards`)) {
        navigate(`/workspaces/${selectedWorkspace._key}/dashboards/${selectedDashboard._key}`);
      } else if (selectedObjectInfos && !location.pathname.includes(`/workspaces/${selectedWorkspace._key}/objects`)) {
        navigate(`/workspaces/${selectedWorkspace._key}/objects`);
      }
    }
  }, [selectedObjectInfos, selectedDashboard]);

  // Personal preferences (view folders) of every object, in a single call.
  // Loaded in parallel with the objects: the sidebar shows the flat list
  // until they arrive, and folders appear right after.
  const fetchPreferences = async () => {
    if (!selectedWorkspace) {
      setWorkspacePreferences([]);
      return;
    }
    const response = await fetchWorkspacePreferences(selectedWorkspace._key);
    if (!response || isCompozzError(response)) {
      setWorkspacePreferences([]);
      return;
    }
    setWorkspacePreferences(response as UserPreferences[]);
  };

  const fetchObjects = async () => {
    try {
      // If no workspace is selected, clear the objects
      if (!selectedWorkspace) {
        setObjectInfos([], null, null, null, []);
        return;
      }

      const url = `${BASE_URL}/model/workspaces/${selectedWorkspace._key}/objects?get-fields=true&get-relations=true&get-views=true&get-shared-objects=true&get-actions=true`;
      const response = await get<ObjectResponse>(url);
      if (isCompozzError(response)) {
        throw response;
      }
      const dashboardsUrl = `${BASE_URL}/model/workspaces/${selectedWorkspace._key}/dashboards`;
      const dashboardsResponse = await get<Dashboard[]>(dashboardsUrl);
      if (isCompozzError(dashboardsResponse)) {
        throw dashboardsResponse;
      }
      if (response) {
        const objectResponse = response as ObjectResponse;
        const objectInfos = objectResponse.objectInfos ?? [];
        const sharedObjectInfos = objectResponse.sharedObjectInfos;
        const selectedObjectId = getSelectedObjectKey(selectedWorkspace?._key);
        const selectedViewId = getSelectedViewKey(selectedWorkspace?._key);
        const selectedDashboardId = getSelectedDashboardKey(selectedWorkspace?._key);
        let selectedObjectInfos = null;
        let selectedView = null;
        let selectedDashboard = null;
        if (dashboardsResponse) {
          const dashboards = dashboardsResponse as Dashboard[];
          if (dashboards && dashboards.length > 0) {
            if (selectedDashboardId) {
              selectedDashboard = dashboards.find(dashboard => dashboard._key === selectedDashboardId) || null;
            }
          }
        }
        if (selectedObjectId) {
          selectedObjectInfos = getObjectInfos(objectInfos, selectedObjectId);
          if (!selectedObjectInfos) {
            selectedObjectInfos = objectInfos[0];
            if (selectedObjectInfos.views && selectedObjectInfos.views.length > 0) {
              selectedView = selectedObjectInfos.views[0];
            }
          } else {
            if (selectedViewId) {
              selectedView = getView(selectedObjectInfos, selectedViewId);
            }
          }
        } else {
          if (!selectedDashboard) {
            selectedObjectInfos = objectInfos[0];
          }
        }
        const { selectedObjectInfos: objectInfoToSelect, selectedView: viewToSelect, selectedDashboard: dashboardToSelect } = checkedSelection(selectedObjectInfos, selectedView, selectedDashboard);
        setObjectInfos(objectInfos, objectInfoToSelect, viewToSelect, dashboardToSelect, sharedObjectInfos);
        updateStorageSelection(selectedWorkspace?._key, objectInfoToSelect?.object?._key, viewToSelect?._key, dashboardToSelect?._key);
      } else {
        setObjectInfos([], null, null, null, []);
      }
    } catch (error) {
      setObjectInfos([], null, null, null, []);
    }
  };

  const fetchWorkspaces = async () => {
    try {
      const response = await get<Workspace[]>(`${BASE_URL}/model/workspaces`);
      if (isCompozzError(response)) {
        throw response;
      }
      const workspaces = response as Workspace[];
      if (workspaces && workspaces.length > 0) {
        setWorkspaces(workspaces);
        let currWorkspace = null;
        if (wsKey) {
          const selectedWorkspace = getWorkspace(workspaces, wsKey);
          if (selectedWorkspace) {
            setSelectedWorkspace(selectedWorkspace);
            currWorkspace = selectedWorkspace;
          } else {
            setSelectedWorkspace(null);
            navigate('/');
          }
        } else {
          const selectedWorkspaceId = localStorage.getItem(SELECTED_WORKSPACE_ID);
          if (selectedWorkspaceId) {
            const selectedWorkspace = getWorkspace(workspaces, selectedWorkspaceId);
            if (selectedWorkspace) {
              setSelectedWorkspace(selectedWorkspace);
              currWorkspace = selectedWorkspace;
            } else {
              setSelectedWorkspace(workspaces[0]);
              currWorkspace = workspaces[0];
            }
          } else {
            setSelectedWorkspace(workspaces[0]);
            currWorkspace = workspaces[0];
          }
        }
        fetchUserRole(currWorkspace?._key);
      }
    } catch (error) {
      setWorkspaces([]);
      setSelectedWorkspace(null);
      updateUserRole(null);
    }
  };

  function getWorkspace(workspaces: Workspace[], workspaceId: string): Workspace | null {
    return workspaces.find(workspace => workspace._key === workspaceId) || null;
  }

  function getObjectInfos(objectInfos: ObjectInfos[] | undefined, objectId: string): ObjectInfos | null {
    return objectInfos?.find(objectInfos => objectInfos.object._key === objectId) || null;
  }

  function getView(objectInfos: ObjectInfos | undefined, viewId: string): View | null {
    return objectInfos?.views?.find(view => view._key === viewId) || null;
  }

  if (loading) {
    return <LoadingWorkspaceSection />;
  }

  const resizePanel = function (size: number, prevSize: number | undefined) {
    if (prevSize && prevSize !== maxSidebarSize && sideBarOpen) {
      localStorage.setItem(APP_SIDEBAR_COLUMN_SIZE + selectedWorkspace?._key, size.toString());
    }
  }

  function onArrowclick() {
    setSideBarOpen(!sideBarOpen);
  }

  function onMobileSidebarToggle() {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  }

  if (selectedWorkspace?.drive) {
    return (
      <div className={TW_App} key={"app-workspace-section-drive-" + selectedWorkspace._key}>
        <WorkspaceHeader
          onMobileSidebarToggle={onMobileSidebarToggle}
          isMobileSidebarOpen={isMobileSidebarOpen}
        />
        <div className={TW_AppMain}>
          <DriveWorkspaceLayout wsKey={selectedWorkspace._key} />
        </div>
      </div>
    );
  }

  return (
    <div className={TW_App} key={"app-workspace-section-" + selectedWorkspace?._key}>
      <WorkspaceHeader
        onMobileSidebarToggle={onMobileSidebarToggle}
        isMobileSidebarOpen={isMobileSidebarOpen}
      />
      {isMobile && (
        <>
          <Routes>
            <Route path="/objects" element={<ObjectModelView />} />
            <Route path="/dashboards/*" element={<Dashboards />} />
            <Route path="/members" element={<Members />} />
            <Route path="/roles" element={<Roles />} />
          </Routes>
        </>
      )}
      {isMobile && isMobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onMobileSidebarToggle}></div>
          <div className="fixed left-0 top-0 h-full w-80 max-w-[80vw] bg-white shadow-lg">
            <Routes>
              <Route path="*" element={<AppSidebar onArrowclick={onMobileSidebarToggle} sideBarOpen={true} isMobile={true} onMobileClose={onMobileSidebarToggle} />} />
            </Routes>
          </div>
        </div>
      )}
      {!isMobile && (
        <ResizablePanelGroup key={"app-sidebar-group-" + selectedWorkspace?._key + '-' + (isMobile ? 'm' : 'd')} className={TW_AppMain + (isMobile ? " hidden" : "")} direction="horizontal">
          <>
            <ResizablePanel key={"app-sidebar-left-" + selectedWorkspace?._key} defaultSize={sideBarOpen ? sidebarColumnSize : maxSidebarSize} minSize={sideBarOpen ? 6 : maxSidebarSize} maxSize={sideBarOpen ? undefined : maxSidebarSize} onResize={resizePanel} ref={leftPanelRef} >
              <Routes>
                <Route path="*" element={<AppSidebar onArrowclick={onArrowclick} sideBarOpen={sideBarOpen} isMobile={isMobile} />} />
              </Routes>
            </ResizablePanel>
            <ResizableHandle />
          </>
          <ResizablePanel key={"app-sidebar-right-" + selectedWorkspace?._key} defaultSize={isMobile ? 100 : (100 - sidebarColumnSize)} className="w-full h-full">
            <Routes>
              <Route path="/objects" element={<ObjectModelView />} />
              <Route path="/dashboards/*" element={<Dashboards />} />
              <Route path="/members" element={<Members />} />
              <Route path="/roles" element={<Roles />} />
            </Routes>
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
};

export default WorkspaceSection;